import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { quotationService } from '../../services/api';

// Initial state
const initialState = {
  quotations: [],
  quotation: null,
  items: [],
  isLoading: false,
  error: null,
  pagination: {
    page: 1,
    pageSize: 10,
    totalCount: 0,
  },
};

// Async thunks
export const fetchQuotations = createAsyncThunk(
  'quotations/fetchQuotations',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await quotationService.getQuotations(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchQuotation = createAsyncThunk(
  'quotations/fetchQuotation',
  async (id, { rejectWithValue }) => {
    try {
      const response = await quotationService.getQuotation(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const createQuotation = createAsyncThunk(
  'quotations/createQuotation',
  async (data, { rejectWithValue }) => {
    try {
      const response = await quotationService.createQuotation(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateQuotation = createAsyncThunk(
  'quotations/updateQuotation',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await quotationService.updateQuotation(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteQuotation = createAsyncThunk(
  'quotations/deleteQuotation',
  async (id, { rejectWithValue }) => {
    try {
      await quotationService.deleteQuotation(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchQuotationItems = createAsyncThunk(
  'quotations/fetchQuotationItems',
  async (id, { rejectWithValue }) => {
    try {
      const response = await quotationService.getQuotationItems(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Quotation slice
const quotationSlice = createSlice({
  name: 'quotations',
  initialState,
  reducers: {
    clearQuotationError: (state) => {
      state.error = null;
    },
    clearQuotation: (state) => {
      state.quotation = null;
      state.items = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch quotations
      .addCase(fetchQuotations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchQuotations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.quotations = action.payload.results || action.payload;
        // Handle pagination if API returns pagination data
        if (action.payload.count !== undefined) {
          state.pagination.totalCount = action.payload.count;
        }
        state.error = null;
      })
      .addCase(fetchQuotations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch quotations';
      })
      // Fetch single quotation
      .addCase(fetchQuotation.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchQuotation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.quotation = action.payload;
        state.error = null;
      })
      .addCase(fetchQuotation.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch quotation';
      })
      // Create quotation
      .addCase(createQuotation.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createQuotation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.quotations.unshift(action.payload);
        state.error = null;
      })
      .addCase(createQuotation.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to create quotation';
      })
      // Update quotation
      .addCase(updateQuotation.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateQuotation.fulfilled, (state, action) => {
        state.isLoading = false;
        // Update the quotation in the list if it exists
        state.quotations = state.quotations.map((quotation) =>
          quotation.id === action.payload.id ? action.payload : quotation
        );
        // Update the current quotation if it's the one being viewed
        if (state.quotation && state.quotation.id === action.payload.id) {
          state.quotation = action.payload;
        }
        state.error = null;
      })
      .addCase(updateQuotation.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to update quotation';
      })
      // Delete quotation
      .addCase(deleteQuotation.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteQuotation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.quotations = state.quotations.filter(
          (quotation) => quotation.id !== action.payload
        );
        // Clear the current quotation if it's the one being deleted
        if (state.quotation && state.quotation.id === action.payload) {
          state.quotation = null;
        }
        state.error = null;
      })
      .addCase(deleteQuotation.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to delete quotation';
      })
      // Fetch quotation items
      .addCase(fetchQuotationItems.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchQuotationItems.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload;
        state.error = null;
      })
      .addCase(fetchQuotationItems.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch quotation items';
      });
  },
});

export const { clearQuotationError, clearQuotation } = quotationSlice.actions;
export default quotationSlice.reducer;
